import React, { useMemo, useState } from 'react';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useCVData } from '../../../../../lib/cv-mock-data';
import { CVDocumentPreview } from '../../../../../components/cv/CVDocumentPreview';
import { CVBottomBar } from '../../../../../components/cv/CVBottomBar';
import { CustomizeBottomSheet } from '../../../../../components/cv/CustomizeBottomSheet';
import { CvShareBottomSheet } from '../../../../../components/cv/CvShareBottomSheet';
import { WebPageHeader } from '../../../../../components/common/WebPageHeader';
import { MaterialSymbol } from '../../../../../components/icons/MaterialSymbol';
import { buildTailoredCvRoute, leaveCvEditor } from '../../../../../lib/cv-target-route';
import { usePublishPageContext } from '../../../../../lib/page-context';
import { CvHandoffSendSheet } from '../../../../../components/cv/CvHandoffSendSheet';
import { WhereHeardPrompt } from '../../../../../components/cv/WhereHeardPrompt';
import { useTailoredCVs, useSendCvHandoff } from '../../../../../hooks/useTailoredCvApi';
import { isCvHandoffSendable } from '../../../../../lib/cv-handoff-ui';
import { useToast } from '../../../../../hooks/useToast';
import { useAuth } from '../../../../../hooks/useAuth';
import { buildPublicCvRouteUrl } from '../../../../../lib/public-web';
import { CvBlankDocumentState } from '../../../../../components/cv/CvBlankDocumentState';
import { hasCvBodyContent } from '../../../../../lib/cv-section-config';

function HeaderShareButton({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className="tabbio-chip"
      onClick={onClick}
      style={{ width: 40, height: 40, padding: 0, justifyContent: 'center' }}
    >
      <MaterialSymbol name={icon} size={18} />
    </button>
  );
}

export default function TailoredCVPreviewWebScreen({
  initialCustomizeOpen = false,
}: {
  initialCustomizeOpen?: boolean;
} = {}) {
  const { t } = useTranslation('seeker');
  const router = useRouter();
  const { id, share, returnTo } = useLocalSearchParams<{ id: string; share?: string; returnTo?: string }>();
  const { profile, sectionsConfig } = useCVData();
  const toast = useToast();
  const { isUaePassVerified } = useAuth();
  const { data: cvs } = useTailoredCVs();
  const currentCv = cvs?.find((cv) => cv.id === id) ?? null;
  const sendHandoff = useSendCvHandoff(id);
  const [showCustomize, setShowCustomize] = useState(initialCustomizeOpen);
  const [showShare, setShowShare] = useState(share === '1');
  const [sendOpen, setSendOpen] = useState(false);
  const publicCvUrl = profile.username
    ? buildPublicCvRouteUrl(profile.username)
    : null;

  // Tell the agent chat column which tailored CV is open, so "tighten this
  // CV" / "fix this CV" pass this cvId to the CV tools.
  usePublishPageContext(
    useMemo(
      () =>
        id
          ? {
              screen: 'cv-tailored' as const,
              entity: {
                type: 'tailoredCv' as const,
                id,
                label: currentCv?.title ?? undefined,
              },
            }
          : null,
      [id, currentCv?.title],
    ),
  );

  const navigateToEdit = (screen: string) => {
    router.push(buildTailoredCvRoute(id, screen));
  };

  const title = profile.name || t('cv.tailored.title');
  const subtitle = profile.headline || undefined;
  const closeCustomize = () => {
    if (initialCustomizeOpen) {
      router.replace(buildTailoredCvRoute(id));
      return;
    }
    setShowCustomize(false);
  };
  const closeShare = () => {
    setShowShare(false);
    if (share === '1') {
      router.replace(buildTailoredCvRoute(id));
    }
  };

  const handleSendHandoff = async (input: {
    recipientEmail: string;
    recipientName?: string | null;
    mode: 'copy' | 'transfer';
  }) => {
    try {
      await sendHandoff.mutateAsync(input);
      toast.success(t('cv.handoff.sendSuccess'));
      setSendOpen(false);
    } catch (error) {
      toast.error(
        t('cv.handoff.sendFailed'),
        error instanceof Error ? error.message : undefined,
      );
    }
  };

  return (
    <div
      className="tabbio-phone-cv-page"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'var(--bg)',
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          // Matches the main CV page (`cv/index.web.tsx`), which sets the same
          // top inset above the document canvas.
          paddingTop: 30,
          paddingBottom: 128,
          boxSizing: 'border-box',
        }}
      >
        <CVDocumentPreview
          onEditSection={navigateToEdit}
          sectionsConfig={sectionsConfig}
          isUaePassVerified={isUaePassVerified}
          enableVerification
          bodyLeadSlot={
            !hasCvBodyContent(profile, sectionsConfig) ? (
              <CvBlankDocumentState
                onAddSections={() => navigateToEdit('edit-sections')}
              />
            ) : undefined
          }
          topSlot={
            <div className="tabbio-page-header-inset">
              <WebPageHeader
                title={title}
                subtitle={subtitle}
                onBack={() => leaveCvEditor(router, returnTo)}
              >
                {currentCv && isCvHandoffSendable(currentCv) ? (
                  <HeaderShareButton
                    icon="send"
                    label={t('cv.handoff.sendInvite')}
                    onClick={() => setSendOpen(true)}
                  />
                ) : null}
                <HeaderShareButton
                  icon="ios_share"
                  label={t('cv.share.shareVia')}
                  onClick={() => setShowShare(true)}
                />
              </WebPageHeader>
            </div>
          }
        />
      </div>

      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 128,
          zIndex: 40,
          pointerEvents: 'none',
          background: 'linear-gradient(to bottom, transparent 0%, var(--bg) 76%)',
        }}
      />
      <CVBottomBar
        placement="content"
        mode="tailored"
        tailoredId={id}
        onCustomizePress={() => setShowCustomize(true)}
      />

      {/* "Where did you hear about us?": once per account, only when this CV
          was opened from My Tabbio (the hub stamps the request). */}
      <WhereHeardPrompt />

      {showCustomize ? (
        <CustomizeBottomSheet onClose={closeCustomize} />
      ) : null}

      {showShare ? (
        <CvShareBottomSheet
          visible={showShare}
          publicUrl={publicCvUrl}
          onClose={closeShare}
        />
      ) : null}

      <CvHandoffSendSheet
        visible={sendOpen}
        onClose={() => setSendOpen(false)}
        initialEmail={currentCv?.handoff?.recipientEmail}
        initialName={currentCv?.handoff?.recipientName}
        initialMode={currentCv?.handoff?.mode ?? 'copy'}
        hasBeenSent={currentCv?.handoff?.status === 'pending'}
        isSending={sendHandoff.isPending}
        onSubmit={handleSendHandoff}
        labels={{
          title: t('cv.handoff.sendTitle'),
          subtitle: t('cv.handoff.sendSubtitle'),
          recipientName: t('cv.handoff.recipientName'),
          recipientNamePlaceholder: t('cv.handoff.recipientNamePlaceholder'),
          recipientEmail: t('cv.handoff.recipientEmail'),
          recipientEmailPlaceholder: t('cv.handoff.recipientEmailPlaceholder'),
          modeLabel: t('cv.handoff.modeLabel'),
          copy: t('cv.handoff.copy'),
          copyDescription: t('cv.handoff.copyDescription'),
          transfer: t('cv.handoff.transfer'),
          transferDescription: t('cv.handoff.transferDescription'),
          send: t('cv.handoff.sendInvite'),
          resend: t('cv.handoff.resendInvite'),
          cancel: t('cv.menu.cancel'),
        }}
      />
    </div>
  );
}
